import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, User, LogOut, UserCircle, Shield, Plane, Hotel, Settings, Home, MapPin, Package, Phone, Languages } from 'lucide-react';
import { CurrencySwitcher } from './CurrencySwitcher';
import { useLang } from '../context/LanguageContext';
import { useBranding } from '../context/BrandingContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

const NAV = [
  { to: '/', key: 'home', icon: Home },
  { to: '/destinations', key: 'destinations', icon: MapPin },
  { to: '/packages', key: 'packages', icon: Package },
  { to: '/flights', key: 'flights', icon: Plane },
  { to: '/hotels', key: 'hotels', icon: Hotel },
  { to: '/contact', key: 'contact', icon: Phone },
];

const LangToggle = ({ className = '' }) => {
  const { lang, setLang } = useLang();
  return (
    <button
      data-testid="lang-toggle"
      onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
      className={`inline-flex items-center gap-1 h-9 px-3 rounded-md border border-gray-200 text-sm font-semibold hover:bg-gray-50 transition-colors ${className}`}
      aria-label="Switch language"
    >
      <Languages className="w-4 h-4" /> {lang === 'en' ? 'عربي' : 'English'}
    </button>
  );
};

const Header = ({ user, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLang();
  const branding = useBranding();

  const go = (path) => { navigate(path); setMobileMenuOpen(false); };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.name} className="h-10 w-auto max-w-[160px] object-contain" />
            ) : (
              <div className="bg-gradient-to-br from-teal-500 to-cyan-600 text-white w-10 h-10 rounded-lg flex items-center justify-center font-bold text-xl">TS</div>
            )}
            <span className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">{branding.name}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map(({ to, key, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                data-testid={`nav-${key}`}
                className={({ isActive }) => `flex items-center gap-1.5 font-medium whitespace-nowrap transition-colors ${isActive ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'}`}
              >
                <Icon className="w-4 h-4" />{t(key)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LangToggle />
            <CurrencySwitcher />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="header-user-menu">
                    {user.picture ? <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" /> : <UserCircle className="w-8 h-8 text-teal-600" />}
                    <span className="font-medium">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/my-bookings')} className="cursor-pointer" data-testid="header-my-bookings"><User className="w-4 h-4 mr-2" />{t('myBookings')}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer" data-testid="header-profile-link"><Settings className="w-4 h-4 mr-2" />{t('myProfile')}</DropdownMenuItem>
                  {user.is_admin && <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer" data-testid="header-admin-link"><Shield className="w-4 h-4 mr-2" />{t('adminPanel')}</DropdownMenuItem>}
                  <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-red-600" data-testid="header-logout"><LogOut className="w-4 h-4 mr-2" />{t('logout')}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button data-testid="header-login-btn" onClick={() => navigate('/login')} className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"><User className="w-4 h-4 mr-2" />{t('login')}</Button>
            )}
          </div>

          <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn" aria-label="Menu">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-gray-200 space-y-1" data-testid="mobile-menu">
            {NAV.map(({ to, key, icon: Icon }) => (
              <button key={to} onClick={() => go(to)} className="w-full flex items-center gap-3 px-2 py-2.5 text-gray-700 hover:text-teal-600 hover:bg-teal-50 rounded-md"><Icon className="w-4 h-4" />{t(key)}</button>
            ))}
            <div className="flex gap-2 px-2 py-2"><LangToggle className="flex-1 justify-center" /><CurrencySwitcher className="flex-1" /></div>
            <div className="pt-3 border-t border-gray-200 px-2">
              {user ? (
                <div className="space-y-1">
                  <button onClick={() => go('/my-bookings')} className="w-full flex items-center gap-3 py-2 text-gray-700"><User className="w-4 h-4" />{t('myBookings')}</button>
                  <button onClick={() => go('/profile')} className="w-full flex items-center gap-3 py-2 text-gray-700"><Settings className="w-4 h-4" />{t('myProfile')}</button>
                  {user.is_admin && <button onClick={() => go('/admin')} className="w-full flex items-center gap-3 py-2 text-gray-700"><Shield className="w-4 h-4" />{t('adminPanel')}</button>}
                  <button onClick={() => { onLogout(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 py-2 text-red-600"><LogOut className="w-4 h-4" />{t('logout')}</button>
                </div>
              ) : (
                <Button onClick={() => go('/login')} className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white"><User className="w-4 h-4 mr-2" />{t('login')}</Button>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
